<script setup lang="ts">
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import type { Feature, FeatureCollection, Point } from 'geojson'
import type {
  GeoJSONSource,
  Map,
  MapMouseEvent,
  Popup,
} from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

type SelectedGeoJsonPoint = Feature<Point, SearchMapItem>

const { items = [], mapCenter, showKeyboardControls = true, showKeyboardHelp = false } = defineProps<{
  items: SearchMapItem[]
  mapCenter?: [number, number]
  showKeyboardControls?: boolean
  // Should the keyboard help be shown when keyboard navigation is active?
  showKeyboardHelp?: boolean
}>()

const { t } = useI18n({
  useScope: 'local',
})

// useMapCenter() returns coordinates as [latitude, longitude]
const [lat, lon] = await useMapCenter()

// Prepare centerCoordinates for Mapbox: it expects [longitude, latitude]
const centerCoordinates = computed<[number, number]>(() => {
  // If parent passed mapCenter ([lat, lon]), swap here:
  if (mapCenter) {
    const [mapLat, mapLon] = mapCenter
    return [mapLon, mapLat]
  }
  // Otherwise fallback to [lon, lat] (swapped) from useMapCenter
  return [lon, lat]
})

// TODO: primary color for Nijmegen is causing an issue.
// leading zero is removed somehow?
const colorMode = useColorMode()
const { primary } = useTailwindColors()

const selectedFeatures = ref<SelectedGeoJsonPoint[] | undefined | null>(null)
const selectedFeatureLatLng = ref()
const mapElement = shallowRef<HTMLElement>()
const geocoderElement = shallowRef<HTMLElement>()
const popupElement = shallowRef<HTMLElement>()
const map = shallowRef<Map>()
const popup = shallowRef<Popup>()
const geocoder = shallowRef<any>()

// Add accessibility-related refs
const accessibilityAnnouncement = ref('')
// Flag used to determine whether keyboard navigation is active
const keyboardNavigationActive = ref(true)
// Flag to show additional keyboard controls UI
const showAdditionalControls = ref(true)
const visualNotification = ref('')
const showVisualNotification = ref(false)
const showListFeaturesButton = ref(false)
// Disabled for now, we might want to enable this later
const screenReaderDetected = ref(false)

/**
 * Show or hide the sidebar so that items can be clicked on
 */
const showSidebar = ref(false) // not sure about the default yet
/**
 * This determines whether the sidebar is on the left or right of the map
 */
const sidebarReversed = ref(false)

// Disabled for now, we might want to enable this later
const showGeocoderElement = false

// TODO: fix typing. Need to look closer look at the docs https://github.com/mapbox/mapbox-gl-js/issues/13203
let mapboxgl: any // was typeof import('mapbox-gl')

const itemsWithLocation = computed(() =>
  items.filter(item => item.latitude && item.longitude),
)

const itemsWithoutLocationCount = computed(
  () => items.length - itemsWithLocation.value.length,
)

const locations = computed(
  () =>
    ({
      type: 'FeatureCollection',
      features: itemsWithLocation.value.map(item => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          // GeoJSON Point coords: [longitude, latitude]
          coordinates: [
            item.longitude ?? centerCoordinates.value[0],
            item.latitude ?? centerCoordinates.value[1],
          ],
        },
        // TODO: use `item` 1:1. Something breaks down if we do.
        properties: {
          id: item.id as string,
          to: item.to,
          title: item.title,
          description: item.description,
          address: item.formattedAddress,
          avatarUrl: item.avatarUrl,
        },
      })),
    }) satisfies FeatureCollection,
)

// Accessibility functions
function announceToScreenReader(message: string) {
  accessibilityAnnouncement.value = message
  // Clear after a short delay to allow for re-announcements
  setTimeout(() => {
    accessibilityAnnouncement.value = ''
  }, 100)
}

function showNotification(message: string) {
  // Show visual notification for all users
  visualNotification.value = message
  showVisualNotification.value = true

  // Also announce to screen readers
  announceToScreenReader(message)

  // Hide notification after 3 seconds
  setTimeout(() => {
    showVisualNotification.value = false
  }, 3000)
}

// Disabled this because I thought it's too hacky.
// Research what Wolf Maps does.
// function detectScreenReader() {
//   // Check for common screen reader indicators
//   const hasScreenReader
//     // Check for NVDA, JAWS, or other screen readers
//     = navigator.userAgent.includes('NVDA')
//     || navigator.userAgent.includes('JAWS')
//     // Check if user prefers reduced motion (often indicates assistive technology)
//     || window.matchMedia('(prefers-reduced-motion: reduce)').matches
//     // Check if high contrast is enabled
//     || window.matchMedia('(prefers-contrast: high)').matches
//     // Check for Windows High Contrast mode
//     || window.matchMedia('(-ms-high-contrast: active)').matches
//     // Check if user has navigated using keyboard (Tab key detection)
//     || document.body.classList.contains('user-is-tabbing')

//   screenReaderDetected.value = hasScreenReader

//   // Also listen for keyboard navigation as an indicator
//   document.addEventListener('keydown', (e) => {
//     if (e.key === 'Tab') {
//       screenReaderDetected.value = true
//       document.body.classList.add('user-is-tabbing')
//     }
//   })

//   // Listen for focus events that might indicate screen reader usage
//   document.addEventListener('focusin', () => {
//     if (document.activeElement && document.activeElement !== document.body) {
//       screenReaderDetected.value = true
//     }
//   })
// }

function getDirectionFromBearing(bearing: number): string {
  const normalizedBearing = ((bearing % 360) + 360) % 360
  if (normalizedBearing >= 337.5 || normalizedBearing < 22.5) return 'north'
  if (normalizedBearing >= 22.5 && normalizedBearing < 67.5) return 'northeast'
  if (normalizedBearing >= 67.5 && normalizedBearing < 112.5) return 'east'
  if (normalizedBearing >= 112.5 && normalizedBearing < 157.5) return 'southeast'
  if (normalizedBearing >= 157.5 && normalizedBearing < 202.5) return 'south'
  if (normalizedBearing >= 202.5 && normalizedBearing < 247.5) return 'southwest'
  if (normalizedBearing >= 247.5 && normalizedBearing < 292.5) return 'west'
  return 'northwest'
}

function getFeaturesInView() {
  if (!map.value) return []

  const bounds = map.value.getBounds()

  if (!bounds) {
    console.warn('Map bounds not available')
  }

  // Query all rendered features in the 'places' layer
  const features = map.value.queryRenderedFeatures({
    layers: ['places'],
  })

  return features.filter((feature) => {
    const coords = (feature.geometry as any).coordinates
    return bounds?.contains([coords[0], coords[1]])
  })
}

function handleKeyboardNavigation(event: KeyboardEvent) {
  if (!map.value) return

  // Handle 'M' key to activate keyboard navigation
  if ((event.key === 'm' || event.key === 'M') && !keyboardNavigationActive.value) {
    event.preventDefault()
    enableKeyboardNavigation()
    showListFeaturesButton.value = true // Show the button when keyboard nav is enabled
    return
  }

  // Only handle other keys if keyboard navigation is active
  if (!keyboardNavigationActive.value) return

  const panDistance = 100
  const rotationDegrees = 15
  // const zoomDelta = 1

  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault()
      map.value.panBy([0, -panDistance])
      announceToScreenReader(t('movingNorth', { zoom: Math.round(map.value.getZoom()) }))
      break

    case 'ArrowDown':
      event.preventDefault()
      map.value.panBy([0, panDistance])
      announceToScreenReader(t('movingSouth', { zoom: Math.round(map.value.getZoom()) }))
      break

    case 'ArrowLeft':
      event.preventDefault()
      if (event.shiftKey) {
        const newBearing = map.value.getBearing() - rotationDegrees
        map.value.easeTo({ bearing: newBearing })
        announceToScreenReader(t('rotatingLeft', { direction: getDirectionFromBearing(newBearing) }))
      }
      else {
        map.value.panBy([-panDistance, 0])
        announceToScreenReader(t('movingWest', { zoom: Math.round(map.value.getZoom()) }))
      }
      break

    case 'ArrowRight':
      event.preventDefault()
      if (event.shiftKey) {
        const newBearing = map.value.getBearing() + rotationDegrees
        map.value.easeTo({ bearing: newBearing })
        announceToScreenReader(t('rotatingRight', { direction: getDirectionFromBearing(newBearing) }))
      }
      else {
        map.value.panBy([panDistance, 0])
        announceToScreenReader(t('movingEast', { zoom: Math.round(map.value.getZoom()) }))
      }
      break

    case '+':
    case '=':
      event.preventDefault()
      map.value.zoomIn()
      announceToScreenReader(t('zoomedIn', { zoom: Math.round(map.value.getZoom()) }))
      break

    case '-':
      event.preventDefault()
      map.value.zoomOut()
      announceToScreenReader(t('zoomedOut', { zoom: Math.round(map.value.getZoom()) }))
      break

    case 'f':
    case 'F':
      event.preventDefault()
      announceFeaturesInView()
      break

    case 'h':
    case 'H':
      event.preventDefault()
      announceHelp()
      break

    case 'Escape':
      event.preventDefault()
      keyboardNavigationActive.value = false
      showAdditionalControls.value = false
      showListFeaturesButton.value = false // Hide the button when keyboard nav is disabled
      announceToScreenReader(t('keyboardNavDisabled'))
      break
  }
}

function announceFeaturesInView() {
  const features = getFeaturesInView()
  if (features.length === 0) {
    announceToScreenReader(t('noFeaturesVisible'))
    return
  }

  const featureNames = features.map(f => f.properties?.title || 'Unnamed location')
  announceToScreenReader(t('featuresVisible', {
    count: features.length,
    features: featureNames.join(', '),
  }))
}

function announceHelp() {
  announceToScreenReader(t('keyboardNavHelp'))
}

function enableKeyboardNavigation() {
  keyboardNavigationActive.value = true
  showAdditionalControls.value = true
  showListFeaturesButton.value = true // Show the button when keyboard nav is enabled
  announceToScreenReader(t('keyboardNavEnabled'))
}

// UI-based navigation functions
function panUp() {
  if (!map.value) return
  map.value.panBy([0, -100])
  announceToScreenReader(t('movingNorth', { zoom: Math.round(map.value.getZoom()) }))
}

function panDown() {
  if (!map.value) return
  map.value.panBy([0, 100])
  announceToScreenReader(t('movingSouth', { zoom: Math.round(map.value.getZoom()) }))
}

function panLeft() {
  if (!map.value) return
  map.value.panBy([-100, 0])
  announceToScreenReader(t('movingWest', { zoom: Math.round(map.value.getZoom()) }))
}

function panRight() {
  if (!map.value) return
  map.value.panBy([100, 0])
  announceToScreenReader(t('movingEast', { zoom: Math.round(map.value.getZoom()) }))
}

function zoomIn() {
  if (!map.value) return
  map.value.zoomIn()
  announceToScreenReader(t('zoomedIn', { zoom: Math.round(map.value.getZoom()) }))
}

function zoomOut() {
  if (!map.value) return
  map.value.zoomOut()
  announceToScreenReader(t('zoomedOut', { zoom: Math.round(map.value.getZoom()) }))
}

function rotateLeft() {
  if (!map.value) return
  const newBearing = map.value.getBearing() - 15
  map.value.easeTo({ bearing: newBearing })
  announceToScreenReader(t('rotatingLeft', { direction: getDirectionFromBearing(newBearing) }))
}

function rotateRight() {
  if (!map.value) return
  const newBearing = map.value.getBearing() + 15
  map.value.easeTo({ bearing: newBearing })
  announceToScreenReader(t('rotatingRight', { direction: getDirectionFromBearing(newBearing) }))
}

function loadLayers() {
  if (!map.value) {
    return
  }

  // Do nothing if source already added
  if (map.value.getSource('places')) {
    return
  }

  // Debug log
  // eslint-disable-next-line no-console
  // console.log('Adding places layer with features:', locations.value.features.length, locations.value)

  map.value.addSource('places', {
    type: 'geojson',
    data: locations.value,
  })

  // Add a layer showing the places.
  map.value.addLayer({
    id: 'places',
    type: 'circle',
    source: 'places',
    paint: {
      'circle-color': primary.value || '#000', // colorMode.value === 'dark' ? primary.value : primary.value,
      'circle-radius': 7,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#ebebeb',
    },
  })

  map.value.on('click', 'places', (e: MapMouseEvent) => {
    const features = e.features?.map(f => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: f.properties as SearchMapItem,
    })) as SelectedGeoJsonPoint[]

    setSelectedFeature(features)
    selectedFeatureLatLng.value = e.lngLat
  })

  // Change the cursor to a pointer when the mouse is over the places layer.
  map.value.on('mouseenter', 'places', () => {
    if (!map.value) {
      return
    }

    map.value.getCanvas().style.cursor = 'pointer'
  })

  // Change it back to a pointer when it leaves.
  map.value.on('mouseleave', 'places', () => {
    if (!map.value) {
      return
    }

    map.value.getCanvas().style.cursor = ''
  })
}

// TODO: we can call this function to set the selected features whenever we want to build the sidebar list of items
function setSelectedFeature(
  feature: SelectedGeoJsonPoint[] | null | undefined,
) {
  selectedFeatures.value = undefined
  selectedFeatures.value = feature

  if (feature && feature[0].geometry) {
    selectedFeatureLatLng.value = feature[0].geometry.coordinates
  }
}

const config = useRuntimeConfig()

// This block will be removed in production build
if (process.dev && !config.public.mapboxToken) {
  console.warn(
    'NUXT_PUBLIC_MAPBOX_TOKEN environment variable is not defined, mapbox features are disabled',
  )
}

onMounted(() => {
  if (!config.public.mapboxToken) {
    return
  }

  Promise.all([
    import('mapbox-gl').then(m => m.default),
    import('@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.min.js').then(
      m => m.default,
    ),
  ]).then(([_mapboxgl, MapboxGeocoder]) => {
    mapboxgl = _mapboxgl
    if (!mapElement.value || !geocoderElement.value) {
      return
    }
    // You can set the NUXT_PUBLIC_MAPBOX_TOKEN inside .env file
    mapboxgl.accessToken = config.public.mapboxToken

    map.value = new mapboxgl.Map({
      container: mapElement.value,
      style:
        colorMode.value === 'dark'
          ? 'mapbox://styles/mapbox/dark-v10'
          : 'mapbox://styles/mapbox/light-v10',
      center: centerCoordinates.value,
      zoom: 10,
    })

    if (!map.value) {
      console.error('Mapbox map could not be initialized')
      return
    }

    // Add accessibility attributes to the map canvas
    const canvas = map.value.getCanvas()
    console.log(`canvas`, canvas)
    canvas.setAttribute('role', 'application')
    canvas.setAttribute('aria-label', `Interactive map showing ${itemsWithLocation.value.length} locations. Press M to activate keyboard navigation.`)
    canvas.setAttribute('tabindex', '0')

    // Announce to screen readers that M can be pressed for more controls when map is focused
    canvas.addEventListener('focus', () => {
      if (!keyboardNavigationActive.value) {
        announceToScreenReader(t('mapFocused', { count: itemsWithLocation.value.length }))
      }
    })

    geocoder.value = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      marker: true,
    })

    map.value.on('style.load', () => {
      console.log('🗺️ style.load')
      // Timeout to ensure styles are loaded before adding layers
      function loadingStyles() {
        if (!map.value?.isStyleLoaded()) {
          setTimeout(loadingStyles, 1500)
          return
        }
        console.log('🗺️ Map style loaded, adding layers')
        loadLayers()
      }
      loadingStyles()
    })

    // Add map event listeners for accessibility announcements
    map.value.on('moveend', () => {
      if (keyboardNavigationActive.value) {
        // const center = map.value!.getCenter()
        const zoom = Math.round(map.value!.getZoom())
        announceToScreenReader(`Map moved. Zoom level ${zoom}`)
      }
    })

    map.value.on('zoomend', () => {
      if (keyboardNavigationActive.value) {
        const zoom = Math.round(map.value!.getZoom())
        announceToScreenReader(`Zoom level ${zoom}`)
      }
    })

    console.log('🗺️ Map initialized with center:', centerCoordinates.value)
    geocoderElement.value.appendChild(geocoder.value.onAdd(map.value))

    // detectScreenReader()
  })
})

const zoomInOnClick = false

const router = useRouter()

watchEffect(
  () => {
    if (!selectedFeatures.value || !popupElement.value || !map.value) {
      return
    }

    const features = selectedFeatures.value
    const { geometry, properties } = features[0]
    const [long, lat] = (geometry as Point).coordinates
    const coordinates: [number, number] = [long, lat]

    if (zoomInOnClick) {
      // Ensure that if the map is zoomed out such that multiple
      // copies of the features are visible, the popup appears
      // over the copy being pointed to.
      if (selectedFeatureLatLng.value) {
        while (Math.abs(selectedFeatureLatLng.value.lng - coordinates[0]) > 180) {
          coordinates[0]
            += selectedFeatureLatLng.value.lng > coordinates[0] ? 360 : -360
        }
      }

      console.log('zooming at: ', properties, coordinates)
      map.value.flyTo({
        center: coordinates,
        zoom: 15,
        bearing: 0,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      })
    }

    // console.log('Creating popup for:', properties)

    if (popup.value) {
      popup.value.remove()
    }

    // Create popup with HTML content for proper sizing
    console.log('🗺️ Creating popup with HTML content for:', properties)
    popup.value = new mapboxgl.Popup({
      className: 'custom-popup', // Add this to help with styling if needed
      maxWidth: '300px', // Adjust this value as needed
    })
      .on('close', () => {
        setSelectedFeature(null)
      })
      .setLngLat(coordinates)
      .setHTML(popupElement.value.innerHTML)
      .addTo(map.value)

    // After popup is added, find the content and add click handlers
    const popupContainer = popup.value?.getElement()
    const popupContent = popupContainer?.querySelector('.mapboxgl-popup-content')

    if (popupContent) {
      popupContent.addEventListener('click', (e) => {
        const clickedElement = e.target as HTMLElement
        const linkElement = clickedElement.closest('a')

        if (linkElement && linkElement.hasAttribute('href')) {
          e.preventDefault()
          const href = linkElement.getAttribute('href')
          if (href) {
            router.push(href)
            popup.value?.remove()
          }
        }
      })

      // Reapply any Vue component styles or classes that might have been lost
      popupContent.querySelectorAll('*[class]').forEach((element) => {
        element.classList.add('popup-content') // Add a marker class if needed
      })
    }

    // popup.value = new mapboxgl.Popup()
    //   // .on('open', () => {
    //   //   selectedFeatureName.value = name
    //   // })
    //   .on('close', () => {
    //     setSelectedFeature(null)
    //   })
    //   .setLngLat(coordinates)
    //   // .setDOMContent(popupContent) // Use setDOMContent instead of setHTML
    //   .setHTML(popupElement.value.innerHTML)
    //   .addTo(map.value)
  },
  { flush: 'post' },
)

watch(
  () => colorMode.value === 'dark',
  () => {
    if (!map.value) {
      return
    }

    if (colorMode.value === 'dark') {
      map.value.setStyle('mapbox://styles/mapbox/dark-v10')
    }
    else {
      map.value.setStyle('mapbox://styles/mapbox/light-v10')
    }
  },
)

/**
 * watch for changes to the location - computed from the items prop and then update the map so it can be rerendered in the background with the new data
 *
 */
watch(locations, (locations) => {
  if (map.value) {
    (map.value.getSource('places') as GeoJSONSource)?.setData(locations)
  }
})
</script>

<template>
  <div
    class="relative col-span-2 flex flex-col gap-6 lg:flex-row"
    :class="[sidebarReversed && '!flex-row-reverse']"
  >
    <!-- Screen reader announcements -->
    <div
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    >
      {{ accessibilityAnnouncement }}
    </div>

    <!-- Visual notification for all users -->
    <div
      v-if="showVisualNotification"
      class="dark:bg-muted-800 border-muted-200 dark:border-muted-700 absolute left-1/2 top-4 z-20 max-w-md -translate-x-1/2 rounded-lg border bg-white p-3 text-center shadow-lg"
      role="status"
      aria-live="polite"
    >
      <BaseParagraph size="sm">
        {{ visualNotification }}
      </BaseParagraph>
    </div>

    <!-- Keyboard navigation instructions -->
    <div
      v-if="keyboardNavigationActive && showKeyboardHelp"
      class="dark:bg-muted-800 border-muted-200 dark:border-muted-700 absolute left-4 top-4 z-10 max-w-sm rounded-lg border bg-white p-4 shadow-lg"
      role="dialog"
      aria-labelledby="keyboard-nav-title"
    >
      <h3 id="keyboard-nav-title" class="mb-2 text-sm font-semibold">
        {{ t('keyboardNavHelp.title') }}
      </h3>
      <ul class="text-muted-600 dark:text-muted-400 space-y-1 text-xs">
        <li>{{ t('keyboardNavHelp.arrowKeys') }}</li>
        <li>{{ t('keyboardNavHelp.shiftArrowKeys') }}</li>
        <li>{{ t('keyboardNavHelp.zoomKeys') }}</li>
        <li>{{ t('keyboardNavHelp.listFeatures') }}</li>
        <li>{{ t('keyboardNavHelp.help') }}</li>
        <li>{{ t('keyboardNavHelp.escape') }}</li>
      </ul>
      <BaseButton
        class="mt-2"
        @click="keyboardNavigationActive = false; showAdditionalControls = false"
      >
        {{ t('keyboardNavHelp.close') }}
      </BaseButton>
    </div>

    <!-- Keyboard Navigation UI Controls -->
    <div
      v-if="showAdditionalControls && showKeyboardControls"
      class="dark:bg-muted-800 border-muted-200 dark:border-muted-700 absolute bottom-12 right-4 z-10 rounded-lg border bg-white p-3 shadow-lg"
      role="group"
      aria-label="Map navigation controls"
    >
      <!-- Directional Controls -->
      <div class="mb-3 grid grid-cols-3 gap-1">
        <!-- Top row -->
        <div />
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="Pan north"
          title="Pan north (↑)"
          @click="panUp"
        >
          <Icon name="heroicons:chevron-up" class="size-4" />
        </button>
        <div />

        <!-- Middle row -->
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="Pan west"
          title="Pan west (←)"
          @click="panLeft"
        >
          <Icon name="heroicons:chevron-left" class="size-4" />
        </button>
        <button
          v-if="screenReaderDetected || showListFeaturesButton"
          class="bg-primary-100 dark:bg-primary-900 hover:bg-primary-200 dark:hover:bg-primary-800 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="List visible features"
          title="List visible features (F)"
          @click="announceFeaturesInView"
        >
          <Icon name="heroicons:list-bullet" class="size-4" />
        </button>
        <div v-else />
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="Pan east"
          title="Pan east (→)"
          @click="panRight"
        >
          <Icon name="heroicons:chevron-right" class="size-4" />
        </button>

        <!-- Bottom row -->
        <div />
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="Pan south"
          title="Pan south (↓)"
          @click="panDown"
        >
          <Icon name="heroicons:chevron-down" class="size-4" />
        </button>
        <div />
      </div>

      <!-- Rotation Controls -->
      <div class="flex justify-between gap-1">
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded text-sm font-bold transition-colors focus:outline-none focus:ring-2"
          aria-label="Zoom in"
          title="Zoom in (+)"
          @click="zoomIn"
        >
          +
        </button>
        <button
          class="bg-muted-100 dark:bg-muted-700 hover:bg-muted-200 dark:hover:bg-muted-600 focus:ring-primary-500 flex size-8 items-center justify-center rounded text-sm font-bold transition-colors focus:outline-none focus:ring-2"
          aria-label="Zoom out"
          title="Zoom out (-)"
          @click="zoomOut"
        >
          -
        </button>
        <button
          v-if="screenReaderDetected"
          class="bg-info-100 dark:bg-info-900 hover:bg-info-200 dark:hover:bg-info-800 focus:ring-primary-500 flex size-8 items-center justify-center rounded transition-colors focus:outline-none focus:ring-2"
          aria-label="Help"
          title="Help (H)"
          @click="announceHelp"
        >
          <Icon name="heroicons:question-mark-circle" class="size-4" />
        </button>
      </div>
    </div>

    <!-- Sidebar card -->
    <div
      v-if="showSidebar"
      class="flex max-w-12 flex-col gap-4 overflow-y-auto"
    >
      <div class="flex max-h-[30rem] flex-col gap-4">
        <BaseCard
          v-for="(feature, key) in locations.features"
          :key="key"
          class="cursor-pointer p-6"
          shape="curved"
          tabindex="0"
          role="button"
          @keydown.space.prevent="setSelectedFeature([feature])"
          @click="setSelectedFeature([feature])"
        >
          <div
            class="mb-4 flex flex-col gap-4 font-sans"
          >
            <BaseParagraph>
              {{ feature.properties.title }}
            </BaseParagraph>
            <BaseParagraph size="sm">
              {{ feature.properties.description }}
            </BaseParagraph>
          </div>
        </BaseCard>
      </div>
    </div>

    <!-- Map content -->
    <div
      class="bg-muted-100 dark:bg-muted-900 ltablet:flex-row flex h-max min-h-[38rem] w-full flex-col lg:flex-row"
    >
      <div class="ltablet:h-auto relative h-96 grow lg:h-auto">
        <div
          ref="mapElement"
          class="absolute inset-0 size-full"
          role="region"
          aria-label="Interactive map"
        />
        <div
          ref="geocoderElement"
          class="geocoder absolute inset-x-0 top-6 mx-auto flex items-center justify-center px-6 sm:px-0"
          :class="!showGeocoderElement ? 'hidden' : ''"
        />
        <div
          ref="popupElement"
          style="display: none; visibility: hidden"
          class="absolute"
        >
          <SearchMapMarker
            :items="selectedFeatures?.map((feature) => feature.properties) || []"
          />
        </div>
      </div>
      <div
        v-if="itemsWithoutLocationCount > 0"
        class="text-muted-700 bg-warning-100 absolute bottom-0 left-0 w-full p-2 text-left text-sm"
        role="status"
        aria-live="polite"
      >
        {{
          t('itemsWithoutLocation', {
            total: items.length,
            count: itemsWithoutLocationCount,
          })
        }}
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "itemsWithoutLocation": "{count} item(s) are not shown on the map because they don't have an address.",
    "keyboardNavEnabled": "Keyboard navigation activated. Use arrow keys to navigate, plus and minus to zoom. Press H for help, Escape to exit.",
    "keyboardNavDisabled": "Keyboard navigation disabled",
    "mapFocused": "Map focused. Press M to activate keyboard navigation. Map shows {count} locations.",
    "movingNorth": "Moving north. Zoom level {zoom}",
    "movingSouth": "Moving south. Zoom level {zoom}",
    "movingEast": "Moving east. Zoom level {zoom}",
    "movingWest": "Moving west. Zoom level {zoom}",
    "rotatingLeft": "Rotating left. Now facing {direction}",
    "rotatingRight": "Rotating right. Now facing {direction}",
    "zoomedIn": "Zoomed in to level {zoom}",
    "zoomedOut": "Zoomed out to level {zoom}",
    "noFeaturesVisible": "No items visible in current map view",
    "featuresVisible": "{count} item(s) visible: {features}",
    "keyboardNavHelp": {
      "title": "Keyboard navigation help",
      "arrowKeys": "Arrow keys: Pan map",
      "shiftArrowKeys": "Shift plus arrow keys: Rotate map",
      "zoomKeys": "Plus and minus: Zoom in/out",
      "listFeatures": "F: Show items in current map view",
      "help": "H: Show help",
      "escape": "Escape: Stop keyboard navigation",
      "close": "Close"
    }
  },
  "nl": {
    "itemsWithoutLocation": "{count} item(s) worden niet op de kaart getoond omdat er geen adres bekend is.",
    "keyboardNavEnabled": "Toetsenbordnavigatie geactiveerd. Gebruik pijltjestoetsen om te navigeren, plus en min om te zoomen. Druk op H voor hulp, Escape om te stoppen.",
    "keyboardNavDisabled": "Toetsenbordnavigatie uitgeschakeld",
    "mapFocused": "Kaart gefocust. Druk op M om toetsenbordnavigatie te activeren. Kaart toont {count} locaties.",
    "movingNorth": "Naar het noorden. Zoomniveau {zoom}",
    "movingSouth": "Naar het zuiden. Zoomniveau {zoom}",
    "movingEast": "Naar het oosten. Zoomniveau {zoom}",
    "movingWest": "Naar het westen. Zoomniveau {zoom}",
    "rotatingLeft": "Naar links roteren. Nu gericht op {direction}",
    "rotatingRight": "Naar rechts roteren. Nu gericht op {direction}",
    "zoomedIn": "Ingezoomd naar niveau {zoom}",
    "zoomedOut": "Uitgezoomd naar niveau {zoom}",
    "noFeaturesVisible": "Geen items zichtbaar in huidige kaartweergave",
    "featuresVisible": "{count} item(s) zichtbaar: {features}",
    "keyboardNavHelp": {
      "title": "Toetsenbordnavigatie",
      "arrowKeys": "Pijltjestoetsen: Pannen",
      "shiftArrowKeys": "Shift plus pijltjestoetsen: Roteren",
      "zoomKeys": "Plus en min: In- en uitzoomen",
      "listFeatures": "F: Toon items in de huidige kaartweergave",
      "help": "H: Toon hulp",
      "escape": "Escape: Stop toetsenbordnavigatie",
      "close": "Sluit"
    }
  }
}
</i18n>

<style scoped>
.mapboxgl-popup .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  @apply relative z-30 -top-0.5;
}
.mapboxgl-popup .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
  @apply relative z-30 -bottom-0.5;
}

.mapboxgl-popup .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  @apply relative z-30 -left-0.5;
}
.mapboxgl-popup .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  @apply relative z-30 -right-0.5;
}

.mapboxgl-popup-close-button {
  @apply h-6 w-6 flex items-center justify-center;
}

.mapboxgl-popup-content {
  @apply rounded-xl shadow-xl shadow-muted-300/30 dark:shadow-muted-900/40 border border-muted-200 dark:border-muted-700 bg-white dark:bg-muted-800 p-4;
  @apply md:min-w-96 min-w-48
}

.mapboxgl-ctrl-geocoder {
  @apply bg-transparent shadow-none;
}

.mapboxgl-ctrl-geocoder input {
  @apply h-12 pl-12 font-sans text-muted-700 dark:text-muted-100 rounded-full bg-white dark:bg-muted-800 border border-muted-200 dark:border-muted-700 shadow-xl shadow-muted-300/30 dark:shadow-muted-900/40 transition-colors duration-300;
}

.mapboxgl-ctrl-geocoder--button {
  @apply bg-transparent;
}

.mapboxgl-ctrl-geocoder--icon-search {
  @apply text-primary-500 fill-current top-3.5 left-3.5 scale-110;
}

.mapboxgl-ctrl-geocoder--icon-close {
  @apply text-muted-400 dark:text-muted-100 fill-current;
}

/* Accessibility improvements */
.mapboxgl-canvas:focus {
  @apply outline-none ring-2 ring-primary-500 ring-offset-2;
}

.mapboxgl-canvas[aria-label] {
  @apply focus:ring-primary-500;
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Ensure keyboard navigation dialog is accessible */
[role="dialog"] {
  @apply focus-within:ring-2 focus-within:ring-primary-500;
}

/* Improve focus visibility for interactive elements */
button:focus,
[role="button"]:focus,
[tabindex="0"]:focus {
  @apply outline-none ring-2 ring-primary-500 ring-offset-2;
}
</style>
